<script setup lang="ts">
import type {MenuProperties} from "@/pages/erp/ds/menu/db/ds-menu-types";
import DsRoleApps from "@/pages/erp/ds/auth/view/_ds-role-apps.vue";
import type {GroupMemberProperties} from "@/pages/erp/ds/auth/db/ds-groupMember-types";
import DsRoleMember from "@/pages/erp/ds/auth/view/ds-role-member.vue";
import EditGroupMemberDrawer from "@/pages/erp/ds/auth/view/ds-groupMember-drawer.vue";
import type { GroupProperties } from '@/pages/erp/ds/auth/db/ds-group-types'
import type {VForm} from "vuetify/components/VForm";

definePage({
  meta: {
    action: 'manage',
    subject: 'DSGROUP',
  },
})


const panel = ref(0)

const route = useRoute('erp-ds-auth-view-role-id')

const roleData = ref<GroupProperties>()

const roleId = ref(route.params.roleId)
const groupId = ref(route.params.roleId)

const compId = ref(useStorage('compNo', 'nstc').value)

/** 加入成員：單位／職稱 picker 排除作廢（後端 queryAll=false） */
const roleMemberTabPickers = computed(() => [
  duUserSinglePicker,
  {
    ...duDepartmentPicker,
    params: {
      ...duDepartmentPicker.params,
      compId: compId.value,
      queryAll: false,
    },
  },
  {
    ...duPositionPicker,
    params: {
      ...duPositionPicker.params,
      compId: compId.value,
      queryAll: false,
    },
  },
])
const menuId = ref()
const menuType = ref('S')
const menuDesc = ref()
const groupDesc = ref('NSTC')

// Data table options
const currentPage = ref(1)
const size = ref(100)
const sortOrder = ref()
const orderByCol = ref()
// champ 後端分頁是從 0 開始
const page = computed((): number => currentPage.value - 1)

// Update data table options
const updateOptions = (options: any) => {
  sortOrder.value = options.sortBy[0]?.key
  orderByCol.value = options.sortBy[0]?.order
}

// 👉 1. 呼叫超級 API (Bundle API)
const { data: bundleData, execute: fetchBundle } = await useApi<any>(createUrl('/sys/getRoleBundle', {
  query: {
    compId,
    roleId,
  },
}))

// 資料映射
if (bundleData.value) {
  roleData.value = bundleData.value.data.role
}

const sysDatas = computed((): MenuProperties[] => bundleData.value?.data?.menus?.filter((m: any) => m.menuType == 'S' && (!m.parent || m.parent == 'root')) || [])
const sysAuth = ref<string[]>(['Administrator'])

// 初始化大目錄的勾選狀態
const isInitialized = ref(false)

watchEffect(() => {
  if (bundleData.value?.data?.menuPermissions) {
    const perms = bundleData.value.data.menuPermissions
    const initialSysAuth = roleId.value === 'Administrator' ? ['Administrator'] : []
    
    sysDatas.value.forEach(item => {
      const gid = toCode(item.authorityGroup)
      if (perms[gid] == 1) {
        initialSysAuth.push(gid)
      }
    })
    sysAuth.value = [...new Set(initialSysAuth)]
    
    // 延遲開啟保護鎖，確保首波資料灌入不會觸發 API
    nextTick(() => {
      isInitialized.value = true
    })
  }
})

// 👉 2. 透過 provide 將資料大禮包分發給所有層級的子元件 (DsRoleApps, DsRoleAction)
provide('roleBundle', computed(() => ({
  menus: bundleData.value?.data?.menus || [],
  menuPermissions: bundleData.value?.data?.menuPermissions || {},
  actionConfigs: bundleData.value?.data?.actionConfigs || {},
  actionAuths: bundleData.value?.data?.actionAuths || {},
})))

const grid1Headers = [
  { title: '#', key: 'id' },
  { title: '成員', key: 'member' },
  { title: '成員類別', key: 'memberType' },
  { title: '狀態', key: 'status', sortable: false},
  { title: '操作', key: 'actions', sortable: false}
]

// Data table options
const memberCurrentPage = ref(1)
const memberSize = ref(10)
const memberSortOrder = ref()
const memberOrderByCol = ref()
// champ 後端分頁是從 0 開始
const memberPage = computed((): number => memberCurrentPage.value - 1)

const { data: groupMembersData, execute: fetchGroupMembers } = await useApi<any>(createUrl('/ds/auth/groupMember/searchGroupMember', {
  query: {
    compId: compId.value,
    groupId_EQ: groupId.value,
    page: memberPage,
    size: memberSize,
    orderByCol: memberSortOrder,
    sortOrder: memberOrderByCol,
  },
}))

const memberDatas = ref<GroupMemberProperties[]>([])

watch(() => groupMembersData.value, (newVal) => {
  if (newVal?.data?.groupMemberPage?.content) {
    memberDatas.value = [...newVal.data.groupMemberPage.content]
  }
}, { immediate: true })
const totalMemberDatas = computed(() => groupMembersData.value?.data?.groupMemberPage?.totalElements || 0)

const isEditGroupMemberVisible = ref(false)
const isNewData = ref(false)
const isCopyData = ref(false)

const editGroupMember = () => {
  isEditGroupMemberVisible.value = true
}

let voData = {}

const createData = () => {
  isEditGroupMemberVisible.value = true
  isNewData.value = true
  isCopyData.value = false
}

const editData = (groupMemberData: GroupMemberProperties) => {
  voData = groupMemberData
  console.log('editGroupMember: ', voData)
  isEditGroupMemberVisible.value = true
  isNewData.value = false
  isCopyData.value = false
}
const addNewData = async (groupMemberData: GroupMemberProperties) => {
  await $api('/ds/auth/groupMember/createGroupMember', {
    method: 'POST',
    body: { groupMember: groupMemberData },
  }).then(() => {
    $swal({
      position: 'top-end',
      icon: 'success',
      title: '新增成功',
      showConfirmButton: false,
      timer: 1500,
      customClass: {
        confirmButton: 'btn btn-primary',
      },
      buttonsStyling: false,
    })
  })

  // refetch data
  fetchGroupMembers()
}

const updateData = async (groupMemberData: GroupMemberProperties) => {
  await $api('/ds/auth/groupMember/updateGroupMember', {
    method: 'PUT',
    body: { groupMember: groupMemberData },
  }).then(() => {
    $swal({
      position: 'top-end',
      icon: 'success',
      title: '修改成功',
      showConfirmButton: false,
      timer: 1500,
      customClass: {
        confirmButton: 'btn btn-primary',
      },
      buttonsStyling: false,
    })
  })

  // refetch data
  fetchGroupMembers()
}

const addGroupMember = async (groupId: string) => {
  await $api('/ds/auth/groupMember/createGroupMember', {
    method: 'POST',
    body: { groupMember: {
        compId: compId.value,
        groupId: groupId,
        member: toCode(roleId.value),
        memberType: 'G',
        status: '1',
        doInsert: true
      } },
  }).then(() => {
    $swal({
      position: 'top-end',
      icon: 'success',
      title: '更新完成',
      showConfirmButton: false,
      timer: 1500,
      customClass: {
        confirmButton: 'btn btn-primary',
      },
      buttonsStyling: false,
    })
  })
}

const deleteData = async (groupMemberData: GroupMemberProperties) => {
  const { compId,groupId,member } = groupMemberData
  $swal({
    title: `確定要刪除 ${toText(member)} 資料?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "確定",
    cancelButtonText: "取消",
    reverseButtons: true
  }).then((result) => {
    if (result.isConfirmed) {
      $api(`/ds/auth/groupMember/deleteGroupMember`, {
        method: 'DELETE',
        query: {
          compId,
          groupId: toCode(groupId),
          member: toCode(member)
        }
      }).then(() => {
        $swal({
          position: 'top-end',
          title: "刪除成功",
          timer: 1500,
          icon: "success"
        });

        // refetch Data
        fetchGroupMembers()
      })
    }
  });

}


const deleteGroupMember = async (groupId: string) => {
  if (roleId.value === 'Administrator') return

  $api(`/ds/auth/groupMember/deleteGroupMember`, {
    method: 'DELETE',
    query: {
      compId: compId.value.trim(),
      groupId: groupId.trim(),
      member: roleId.value.trim()
    }
  }).then(() => {
    $swal({
      position: 'top-end',
      icon: 'success',
      title: '更新完成',
      showConfirmButton: false,
      timer: 1500,
      customClass: {
        confirmButton: 'btn btn-primary',
      },
      buttonsStyling: false,
    })
  })
}

watch(sysAuth, (value, oldValue) => {
  if (!isInitialized.value || roleId.value === 'Administrator') return

  let newAuth: string[] = [], delAuth: string[] = []
  value.forEach(it => {
    if(!oldValue.includes(it)) {
      newAuth.push(it)
      addGroupMember(it)
    }
  })
  oldValue.forEach(it => {
    if(!value.includes(it) && !delAuth.includes(it)) {
      delAuth.push(it)
      deleteGroupMember(it)
    }
  })
})

const memberTypeText = {
  'E': '人員',
  'D': '單位',
  'G': '群組',
  'P': '職稱'
}

const isFormValid = ref(false)
const refForm = ref<VForm>()

const onSubmit = () => {
  refForm.value?.validate().then(({ valid }) => {
    if (valid) {
      $api('/ds/auth/group/updateGroup', {
        method: 'PUT',
        body: { group: roleData.value },
      }).then(() => {
        $swal({
          position: 'top-end',
          icon: 'success',
          title: '修改成功',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
      })
    }
  })
}

const memberTypes = {
  '使用者': 'E',
  '單位': 'D',
  '群組': 'G',
  '職稱': 'P'
}

const onPickMember = (pickValue: any) => {
  if (pickValue) {
    let memberType = memberTypes[pickValue.name as keyof typeof memberTypes]
    pickValue.val.forEach( (it: any) => {
      console.log('Processing item:', it, 'Type:', typeof it)
      
      // 處理不同的資料類型
      let memberCode
      if (typeof it === 'string') {
        memberCode = toCode(it)
      } else if (typeof it === 'object' && it !== null) {
        // 如果是物件，嘗試取得常見的代碼欄位
        memberCode = it.value || it.code || it.id || it.postNo || it.depNo || it.userNo || it.groupId
        console.log('Extracted from object:', memberCode)
      } else {
        memberCode = it
      }
      
      // 轉換為字串並驗證
      const memberCodeStr = String(memberCode)
      
      // 驗證成員代號
      if (!memberCode || memberCodeStr === 'undefined' || memberCodeStr === 'null' || memberCodeStr === '') {
        console.error('Invalid member code - skipping:', it, 'memberCode:', memberCode, 'memberCodeStr:', memberCodeStr)
        $swal({
          position: 'top-end',
          icon: 'warning',
          title: '資料異常',
          text: `「${toText(it)}」的代碼為空，已略過此項目。請聯絡管理員修正資料。`,
          showConfirmButton: true,
          timer: 3000,
        })
        return // 跳過這個項目，繼續處理下一個
      }
      
      const groupMemberData: GroupMemberProperties = {
        compId: useStorage('compNo', 'nstc').value,
        groupId: groupId.value,
        member: memberCodeStr,
        memberType: memberType,
        status: 1,
        expired: '21991231',
      }

      console.log('Creating group member:', groupMemberData)
      addNewData(groupMemberData)
    })


  }
}
const handleCheckboxClick = (event: Event) => {
  console.log('Checkbox clicked');
  // 阻止事件冒泡
  event.stopPropagation();
};
</script>

<template>
  <VCard flat>
    <VCardItem>
      <VForm
        ref="refForm"
        v-model="isFormValid"
        @submit.prevent="onSubmit"
      >
        <VCardText class="d-flex flex-wrap gap-2">
          <p style="font-size: 36px;">角色授權</p>
          <VSpacer />

          <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
            <VBtn
              variant="outlined"
              :to="{ name: 'erp-ds-auth-view-ds-role'}"
            >
              返回
            </VBtn>
            <!-- 👉 Add button -->
            <VBtn
              type="submit"
            >
              儲存
            </VBtn>
          </div>
        </VCardText>
        <VRow v-if="roleData">
          <VCol cols="5">
            <VRow no-gutters>
              <!-- 👉 First Name -->
              <VCol
                cols="2"
                md="2"
                class="d-flex align-items-center"
              >
                <label
                  class="v-label text-body-2 text-high-emphasis"
                  for="groupId"
                >角色代號</label>
              </VCol>

              <VCol
                cols="6"
                md="6"
              >
                <AppTextField
                  id="groupId"
                  v-model="roleData.groupId"
                  placeholder="請輸入角色代號"
                  persistent-placeholder
                />
              </VCol>
            </VRow>
          </VCol>
          <VCol cols="5">
            <VRow no-gutters>
              <VCol
                cols="2"
                md="2"
                class="d-flex align-items-center"
              >
                <label
                  class="v-label text-body-2 text-high-emphasis"
                  for="groupDesc"
                >角色說明</label>
              </VCol>

              <VCol
                cols="6"
                md="6"
              >
                <AppTextField
                  id="groupDesc"
                  v-model="roleData.groupDesc"
                  placeholder="請輸入角色說明"
                  persistent-placeholder
                />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
      </VForm>
    </VCardItem>
  </VCard>
  <VDivider />
  <VRow>
    <VCol cols="8">
      <VExpansionPanels v-model="panel">
        <!-- SECTION Delivery Address -->
        <VExpansionPanel v-for="item in sysDatas">
          <VExpansionPanelTitle>
            <VRow>
              <VCol cols="2">
                <VLabel class="text-body-1 text-high-emphasis">{{ item.menuDesc }}</VLabel>
              </VCol>
              <VCol cols="2">
                <VCheckbox :disabled="roleId == 'Administrator'" v-model="sysAuth" :value="roleId == 'Administrator' ? 'Administrator': toCode(item.authorityGroup)" label="授權使用" @click.stop="handleCheckboxClick" />
              </VCol>
            </VRow>
          </VExpansionPanelTitle>

          <VExpansionPanelText>
            <DsRoleApps :menu-id="item.menuId" :member-id="roleId"/>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>
    </VCol>
    <VCol  cols="4">
      <VCard class="mb-6">
        <VCardText class="d-flex flex-wrap gap-4">
          <h5 class="font-weight-medium text-lg mb-6">
            成員
          </h5>
          <VSpacer />

          <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
            <!-- 👉 Add button -->
            <VBtn
              prepend-icon="tabler-plus"
              @click="createData"
            >
              加入成員
            </VBtn>
          </div>
        </VCardText>

        <VDivider />

        <!-- SECTION datatable -->
        <VDataTableServer
          v-model:items-per-page="memberSize"
          v-model:page="memberCurrentPage"
          :items="memberDatas"
          :items-length="totalMemberDatas"
          :headers="grid1Headers"
          class="text-no-wrap"
          @update:options="updateOptions"
        >

          <template #item.id="{ item, index }">
            <div class="text-body-1 text-high-emphasis">
              {{ index + 1 }}
            </div>
          </template>


          <template #item.member="{ item }">
            <div class="text-body-1 text-high-emphasis">
              {{ toText(item.member) }}
            </div>
          </template>

          <template #item.memberType="{ item }">
            <div class="text-body-1 text-high-emphasis">
              {{ memberTypeText[item.memberType as keyof typeof memberTypeText] }}
            </div>
          </template>

          <template #item.status="{ item, index }">
            <VSelect
              v-model="memberDatas[index].status"
              :items="[{value:0,label:'作廢'},{value:1,label:'有效'},{value:2,label:'無選單'}]"
              item-title="label"
              item-value="value"
              placeholder="請選擇存取權限"
              density="compact"
              hide-details
              style="min-width: 83px;"
              @update:model-value="updateData(memberDatas[index])"
            />
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }">
            <VBtn
              variant="outlined"
              density="compact"
              @click.stop="deleteData(item)"
            >
              刪除
            </VBtn>
          </template>

          <!-- pagination -->
          <template #bottom>
            <TablePagination
              v-model:page="memberCurrentPage"
              v-model:size="memberSize"
              :items-per-page="memberSize"
              :total-items="totalMemberDatas"
            />
          </template>
        </VDataTableServer>
        <!-- SECTION -->
      </VCard>
    </VCol>
  </VRow>
<!--  <DsRoleMember-->
<!--    v-if="isEditGroupMemberVisible"-->
<!--    v-model:isDialogVisible="isEditGroupMemberVisible"-->
<!--    :group-id="roleId"-->
<!--    :groupMember-data="voData"-->
<!--    :is-new="isNewData"-->
<!--    :is-copy="isCopyData"-->
<!--    @create-data="addNewData"-->
<!--    @update-data="updateData"-->
<!--  />-->
  <de-tabs-picker-dialog
    v-model:isDialogVisible="isEditGroupMemberVisible"
    titleText="授權成員"
    :pickers="roleMemberTabPickers"
    :multiple="true"
    @pickValue="onPickMember"
    />
</template>
